import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import PublicLayout, { SUPPORT_EMAIL } from '../components/PublicLayout';
import logoMark from '../assets/logo-mark.png';

/**
 * Store links. Leave empty until the listing is live — the badge then shows
 * "Coming soon" instead of a dead link.
 */
const APP_STORE_URL = '';
const PLAY_STORE_URL = '';

const FEATURES = [
  {
    icon: 'search',
    title: 'Find your next home',
    text: 'Browse rentals with photos, pricing, beds, baths, pet policy and lease terms. Save favorites and get alerts when new matches go live.',
  },
  {
    icon: 'file-text',
    title: 'Apply in minutes',
    text: 'One secure rental application with your employment, income and rental history. Upload documents, pay the fee and track your status.',
  },
  {
    icon: 'wrench',
    title: 'Maintenance on demand',
    text: 'Request plumbing, electrical and other repairs with photos and a preferred time slot, then follow the job from request to done.',
  },
  {
    icon: 'scale',
    title: 'Legal services',
    text: 'Get help with lease reviews, disputes and other rental legal matters from vetted providers, booked right inside the app.',
  },
  {
    icon: 'message-square',
    title: 'Talk to merchants',
    text: 'Send inquiries straight to the listing merchant and get notified the moment they respond.',
  },
  {
    icon: 'shield',
    title: 'Secure payments',
    text: 'Application fees and service orders are paid through Stripe. Your card details never touch our servers.',
  },
];

const StoreBadge: React.FC<{ href: string; store: string; sub: string }> = ({ href, store, sub }) => {
  const inner = (
    <>
      <span className="text-[11px] leading-none opacity-80">{href ? sub : 'Coming soon on'}</span>
      <span className="text-base font-bold leading-tight">{store}</span>
    </>
  );
  const cls =
    'flex flex-col items-start px-5 py-2.5 rounded-xl no-underline min-w-[170px] text-white';
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={{ background: 'var(--ink)' }}>
      {inner}
    </a>
  ) : (
    <div className={cls} style={{ background: 'var(--g600)' }} aria-disabled="true">
      {inner}
    </div>
  );
};

const AppLanding: React.FC = () => (
  <PublicLayout title="The Rentagade App">
    {/* Hero */}
    <section className="bg-white border-b border-g200">
      <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
        <div>
          <div
            className="inline-block text-[12px] font-bold uppercase tracking-wider text-brand px-3 py-1 rounded-full mb-4"
            style={{ background: 'var(--brand-bg)' }}
          >
            Renting, simplified
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink leading-tight mb-4">
            Find, apply and live better — all from one app.
          </h1>
          <p className="text-lg text-g600 mb-7 leading-relaxed">
            Rentagade connects renters with trusted merchants and landlords. Search
            listings, submit applications, and book maintenance or legal help
            without the paperwork chase.
          </p>
          <div className="flex flex-wrap gap-3">
            <StoreBadge href={APP_STORE_URL} store="App Store" sub="Download on the" />
            <StoreBadge href={PLAY_STORE_URL} store="Google Play" sub="Get it on" />
          </div>
        </div>
        <div className="flex justify-center">
          <div
            className="w-56 h-56 sm:w-64 sm:h-64 rounded-[48px] flex items-center justify-center"
            style={{ background: 'var(--brand-bg)' }}
          >
            <img src={logoMark} alt="Rentagade app" className="w-36 h-36 sm:w-40 sm:h-40 rounded-full object-contain" />
          </div>
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="max-w-5xl mx-auto px-4 pt-14">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-ink text-center mb-2">
        Everything your rental needs
      </h2>
      <p className="text-g500 text-center mb-10">From the first search to move-in and beyond.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f) => (
          <div key={f.title} className="card p-6">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{ background: 'var(--brand-bg)' }}
            >
              <Icon name={f.icon} size={20} color="var(--brand)" />
            </div>
            <div className="font-bold text-ink mb-1.5">{f.title}</div>
            <div className="text-sm text-g600 leading-relaxed">{f.text}</div>
          </div>
        ))}
      </div>
    </section>

    {/* Agents CTA */}
    <section className="max-w-5xl mx-auto px-4 pt-12">
      <div className="card p-7 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="text-xl font-extrabold text-ink mb-1.5">Are you a merchant or landlord?</div>
          <div className="text-g600 max-w-xl">
            List properties, receive inquiries and review applications from the
            Rentagade merchant portal.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/agent/register" className="btn btn-primary no-underline">
            Become a merchant
          </Link>
          <Link to="/agent/login" className="btn btn-secondary no-underline">
            Merchant sign in
          </Link>
        </div>
      </div>
    </section>

    {/* Help */}
    <section className="max-w-5xl mx-auto px-4 pt-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Link to="/faq" className="card p-6 flex items-center gap-4 no-underline hover:shadow-md transition-shadow">
          <Icon name="message-square" size={22} color="var(--brand)" />
          <div className="flex-1">
            <div className="font-bold text-ink">FAQ</div>
            <div className="text-sm text-g500">Answers to common questions</div>
          </div>
          <Icon name="chevron-right" size={18} color="var(--g400)" />
        </Link>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="card p-6 flex items-center gap-4 no-underline hover:shadow-md transition-shadow"
        >
          <Icon name="mail" size={22} color="var(--brand)" />
          <div className="flex-1">
            <div className="font-bold text-ink">Contact support</div>
            <div className="text-sm text-g500">{SUPPORT_EMAIL}</div>
          </div>
          <Icon name="chevron-right" size={18} color="var(--g400)" />
        </a>
      </div>
    </section>
  </PublicLayout>
);

export default AppLanding;
